import { SitePerspectivePhase, SitePerspectiveProjectType, SitePerspectiveType } from '@the-real-insight/tri-model';

import { authenticatedFetch } from '../JWTAuthService';
import { PATIENT_REQUEST } from '../utilities/requestTimeout';
import { configuration } from '../Configuration';

/**
 * Reading and writing a site's perspectives (`GET`/`POST /site-perspectives`).
 *
 * The address plus project type, phase and type identifies a perspective, so a save is an upsert
 * and the caller never has to know whether it is creating or editing.
 */

const baseUrl = configuration.serverUrl;

export interface SiteAddressParts {
    street?: string;
    streetNumber?: string;
    postalCode?: string;
    city?: string;
    country?: string;
}

export interface PerspectiveScope extends SiteAddressParts {
    projectType: SitePerspectiveProjectType;
    phase: SitePerspectivePhase;
}

/** Stored values by perspective type — everything a step's tiles need in one read. */
export type PerspectiveValues = Partial<Record<SitePerspectiveType, Record<string, any>>>;

export async function getSitePerspectives(scope: PerspectiveScope): Promise<PerspectiveValues> {
    const parameters = new URLSearchParams();
    Object.entries(scope).forEach(([key, value]) => {
        if (value) parameters.set(key, String(value));
    });

    const response = await authenticatedFetch(`${baseUrl}/site-perspectives?${parameters.toString()}`, {
        method: 'GET',
    });

    if (!response || !response.ok) {
        throw new Error(`Perspektiven konnten nicht geladen werden (${response?.status ?? 'keine Antwort'})`);
    }

    const payload = await response.json();
    const byType: PerspectiveValues = {};

    (payload?.perspectives ?? []).forEach((perspective: any) => {
        if (perspective?.type) {
            byType[perspective.type as SitePerspectiveType] = perspective;
        }
    });

    return byType;
}

export async function saveSitePerspective(
    scope: PerspectiveScope,
    type: SitePerspectiveType,
    values: Record<string, any>,
    /**
     * A reason per field, for the fields that replace something.
     *
     * Sent beside the values rather than mixed into them: a comment is about the change, not part
     * of it, and the server records it on the change entry through `changeField`.
     */
    comments?: Record<string, string>,
): Promise<Record<string, any>> {
    const response = await authenticatedFetch(`${baseUrl}/site-perspectives`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...scope, type, values, comments }),
    });

    if (!response || !response.ok) {
        const detail = await response?.json().catch(() => null);
        throw new Error(
            detail?.error ?? `Perspektive konnte nicht gespeichert werden (${response?.status ?? 'keine Antwort'})`,
        );
    }

    const payload = await response.json();

    // The endpoint refuses values it does not recognise and names them. Throwing them away here is
    // what makes a rejected field look like a save that succeeded and did nothing.
    if (Array.isArray(payload?.ignored) && payload.ignored.length > 0) {
        throw new Error(`Nicht gespeichert: ${payload.ignored.join(', ')}`);
    }

    return payload?.perspective ?? {};
}

/** Wie die schreibenden Wege antworten: die Perspektive, mit aufgelösten Bindungen. */
const perspectiveFrom = async (response: Response | undefined, what: string): Promise<Record<string, any>> => {
    if (!response || !response.ok) {
        const detail = await response?.json().catch(() => null);
        throw new Error(detail?.error ?? `${what} (${response?.status ?? 'keine Antwort'})`);
    }
    const payload = await response.json();
    return payload?.perspective ?? {};
};

/**
 * Ein Element einer Liste anlegen (`item`) oder entfernen (`itemKey`).
 *
 * Eigene Wege statt der ganzen Liste: Zwei Menschen, die gleichzeitig je einen Eintrag anlegen,
 * überschrieben sich sonst. Jeder Weg steht als eigener Eintrag in der Historie.
 */
export async function changeListItem(
    scope: PerspectiveScope,
    type: SitePerspectiveType,
    name: string,
    change: { add: Record<string, any> } | { remove: string },
    comment?: string,
): Promise<Record<string, any>> {
    const adding = 'add' in change;
    const response = await authenticatedFetch(`${baseUrl}/site-perspectives/list-items`, {
        method: adding ? 'POST' : 'DELETE',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...scope, type, name, ...(adding ? { item: change.add } : { itemKey: change.remove }), comment }),
    });
    return perspectiveFrom(response, adding ? 'Eintrag konnte nicht angelegt werden' : 'Eintrag konnte nicht entfernt werden');
}

/** Ein Wert an einer gebundenen Instanz — eine Zeile eines Slots, der eine Zeile je Instanz zeigt. */
export async function saveInstanceValue(
    scope: PerspectiveScope,
    type: SitePerspectiveType,
    name: string,
    ref: { _id: string; type: string },
    value: any,
    comment?: string,
): Promise<Record<string, any>> {
    const response = await authenticatedFetch(`${baseUrl}/site-perspectives/instance-value`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...scope, type, name, ref, value, comment }),
    });
    return perspectiveFrom(response, 'Wert konnte nicht gespeichert werden');
}

export type ActivationEvent =
    | { type: 'start'; total: number }
    | { type: 'step'; key: string; label: string; detail?: string; index: number; total: number }
    | { type: 'error'; key: string; label: string; message: string }
    | { type: 'done' };

/**
 * Activate a phase, reporting each step as the server finishes it.
 *
 * The response is newline-delimited JSON, read as it arrives rather than awaited whole — that is
 * the entire point of the endpoint streaming. A chunk can split a line anywhere, so the tail is
 * carried over to the next read instead of being parsed half-finished.
 */
export async function activatePhase(
    scope: PerspectiveScope,
    onEvent: (event: ActivationEvent) => void,
): Promise<void> {
    const response = await authenticatedFetch(`${baseUrl}/site-perspectives/activate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(scope),
        // Ohne Zeitgrenze: Diese Antwort ist ein Strom, der seinen Fortschritt meldet, solange die
        // Aktivierung läuft. Die Grenze für gewöhnliche Abfragen schnitte ihn mitten durch.
        signal: PATIENT_REQUEST,
    });

    if (!response || !response.ok || !response.body) {
        throw new Error(`Aktivierung fehlgeschlagen (${response?.status ?? 'keine Antwort'})`);
    }

    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let rest = '';

    for (;;) {
        const { done, value } = await reader.read();
        if (done) break;

        rest += decoder.decode(value, { stream: true });
        const lines = rest.split('\n');
        rest = lines.pop() ?? '';

        lines
            .map((line) => line.trim())
            .filter((line) => line.length > 0)
            .forEach((line) => {
                try {
                    onEvent(JSON.parse(line));
                } catch (error) {
                    console.warn('Could not read an activation event', line, error);
                }
            });
    }

    // Process any remaining content in the buffer.
    if (rest.trim().length > 0) {
        try {
            onEvent(JSON.parse(rest.trim()));
        } catch (error) {
            console.warn('Could not read the final activation event', rest, error);
        }
    }
}

/**
 * One open (or finished) task of the phase, as the project-status perspective lists it.
 *
 * These are not values of the perspective — they are the checklists the activation created, read
 * through the phase they were created for. Nothing here is typed in, which is why there is no
 * counterpart in `saveSitePerspective`.
 */
export interface PhaseChecklistRow {
    checklistId: string;
    checklist: string;
    task: string;
    role: string | null;
    /** ISO date, or null where the checklist carries no deadline. */
    due: string | null;
    done: boolean;
}

export async function getPhaseChecklists(scope: PerspectiveScope): Promise<PhaseChecklistRow[]> {
    const parameters = new URLSearchParams();
    Object.entries(scope).forEach(([key, value]) => {
        if (value) parameters.set(key, String(value));
    });

    const response = await authenticatedFetch(`${baseUrl}/site-phases/checklists?${parameters.toString()}`, {
        method: 'GET',
    });

    if (!response || !response.ok) {
        throw new Error(`Checklisten konnten nicht geladen werden (${response?.status ?? 'keine Antwort'})`);
    }

    const payload = await response.json();
    return Array.isArray(payload?.rows) ? payload.rows : [];
}

export interface PhaseDeletionResult {
    perspectives: number;
    checklists: number;
    phases: number;
}

/**
 * Delete an activated phase with everything the activation produced.
 *
 * The id is what the confirmation dialog makes the user type, and it is also all the endpoint
 * takes: what gets deleted is derived from the stored phase, not from anything sent along here.
 */
export async function deleteSitePhase(sitePhaseId: string): Promise<PhaseDeletionResult> {
    const response = await authenticatedFetch(`${baseUrl}/site-phases/${encodeURIComponent(sitePhaseId)}`, {
        method: 'DELETE',
    });

    if (!response || !response.ok) {
        const detail = await response?.json().catch(() => null);
        throw new Error(
            detail?.error ?? `Phase konnte nicht gelöscht werden (${response?.status ?? 'keine Antwort'})`,
        );
    }

    return response.json();
}
